import { AddProfileModal } from '@/components/AddProfileModal';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, ChevronDown, Plus, Search, ShieldCheck, UsersRound, X } from 'lucide-react';
import { usePlayers, type PlayersFilters } from '@/hooks/usePlayers';
import { usePlayerActivity } from '@/hooks/usePlayerActivity';
import { PlayerRosterRow } from '@/components/PlayerRosterRow';
import { ClientActivityWall } from '@/components/ClientActivityWall';

const PEOPLE_GROUPS = [
  { value: 'ALL', label: 'All' },
  { value: 'NHL', label: 'NHL' },
  { value: 'AHL', label: 'AHL' },
  { value: 'ECHL', label: 'ECHL' },
  { value: 'EUROPE', label: 'Europe' },
  { value: 'NCAA', label: 'NCAA' },
  { value: 'JUNIOR', label: 'Junior' },
  { value: 'USHL', label: 'USHL' },
  { value: 'WHL', label: 'WHL' },
  { value: 'CHL', label: 'CHL' },
  { value: 'NTDP', label: 'NTDP' },
  { value: 'YOUTH', label: 'Youth' },
  { value: 'PWHL', label: 'PWHL' },
  { value: 'PROSPECT', label: 'Prospect' },
] as const;

const CLASSIFICATIONS = [
  { value: 'pro_men', label: 'Pro Men' },
  { value: 'pro_women', label: 'Pro Women' },
  { value: 'prospect', label: 'Prospect' },
] as const;

const PRIORITIES = [
  { value: '0', label: 'P0' },
  { value: '1', label: 'P1' },
  { value: '2', label: 'P2' },
] as const;

const SORT_OPTIONS = [
  { value: 'last_name', label: 'A-Z' },
  { value: 'league', label: 'League / team' },
  { value: 'priority', label: 'Prospect priority' },
  { value: 'updated', label: 'Recently updated' },
] as const;

function readArrayParam(p: URLSearchParams, key: string): string[] {
  return p.getAll(key);
}

export function People() {
  const [params, setParams] = useSearchParams();

  const search = params.get('search') ?? '';
  const classification = readArrayParam(params, 'classification');
  const league = readArrayParam(params, 'league');
  const priority = readArrayParam(params, 'priority');
  const hasInjury = params.get('hasInjury') === '1';
  const status = (params.get('status') as 'active' | 'archived' | null) ?? 'active';
  const group =
    (params.get('group') as PlayersFilters['group'] | null) && PEOPLE_GROUPS.some((g) => g.value === params.get('group'))
      ? (params.get('group') as PlayersFilters['group'])
      : 'ALL';
  const sort =
    (params.get('sort') as 'priority' | 'last_name' | 'league' | 'updated' | null) ?? 'last_name';

  const [searchInput, setSearchInput] = useState(search);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [addingProfile, setAddingProfile] = useState(false);
  useEffect(() => setSearchInput(search), [search]);

  // Debounce search → URL state
  useEffect(() => {
    if (searchInput === search) return;
    const t = setTimeout(() => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (searchInput) p.set('search', searchInput);
          else p.delete('search');
          return p;
        },
        { replace: true }
      );
    }, 200);
    return () => clearTimeout(t);
  }, [searchInput, search, setParams]);

  // Hide priority chips when no prospect filter is active (per Mockup Spec).
  const showPriority = classification.includes('prospect');

  const filters: PlayersFilters = useMemo(
    () => ({
      search: search || undefined,
      classification: classification.length > 0 ? classification : undefined,
      league: league.length > 0 ? league : undefined,
      priority:
        showPriority && priority.length > 0 ? priority.map((p) => Number.parseInt(p, 10)) : undefined,
      status,
      hasInjury,
      group,
      sort,
    }),
    [search, classification, league, priority, showPriority, status, hasInjury, group, sort]
  );

  const allPlayers = usePlayers({ status: 'active', sort: 'league' });
  const groupCounts = useMemo(() => {
    const counts = new Map<string, number>(PEOPLE_GROUPS.map((g) => [g.value, 0]));
    const items = allPlayers.data?.items ?? [];
    counts.set('ALL', items.length);
    for (const item of items) {
      const league = item.currentLeague ?? '';
      if (league === 'NHL') counts.set('NHL', (counts.get('NHL') ?? 0) + 1);
      if (league === 'AHL') counts.set('AHL', (counts.get('AHL') ?? 0) + 1);
      if (league === 'ECHL') counts.set('ECHL', (counts.get('ECHL') ?? 0) + 1);
      if (league === 'PWHL') counts.set('PWHL', (counts.get('PWHL') ?? 0) + 1);
      if (isEuropeLeague(league)) counts.set('EUROPE', (counts.get('EUROPE') ?? 0) + 1);
      if (isNcaaLeague(league, item.currentTeam)) counts.set('NCAA', (counts.get('NCAA') ?? 0) + 1);
      if (isJuniorLeague(league, item.currentTeam)) counts.set('JUNIOR', (counts.get('JUNIOR') ?? 0) + 1);
      if (isUshlLeague(league)) counts.set('USHL', (counts.get('USHL') ?? 0) + 1);
      if (isWhlLeague(league)) counts.set('WHL', (counts.get('WHL') ?? 0) + 1);
      if (isChlLeague(league)) counts.set('CHL', (counts.get('CHL') ?? 0) + 1);
      if (isNtdpLeague(league, item.currentTeam)) counts.set('NTDP', (counts.get('NTDP') ?? 0) + 1);
      if (league === 'YOUTH') counts.set('YOUTH', (counts.get('YOUTH') ?? 0) + 1);
      if (item.classification === 'prospect') counts.set('PROSPECT', (counts.get('PROSPECT') ?? 0) + 1);
    }
    return counts;
  }, [allPlayers.data?.items]);

  const activeCount = allPlayers.data?.total ?? 0;
  const nhlCount = groupCounts.get('NHL') ?? 0;
  const prospectCount = groupCounts.get('PROSPECT') ?? 0;
  const injuredCount = useMemo(
    () => (allPlayers.data?.items ?? []).filter((item) => item.hasActiveInjury === 1).length,
    [allPlayers.data?.items]
  );

  const { data, isLoading, isError } = usePlayers(filters);
  const activity = usePlayerActivity({ days: 7, limit: 120 });

  function toggleParam(key: string, value: string) {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        const current = p.getAll(key);
        if (current.includes(value)) {
          p.delete(key);
          for (const v of current) if (v !== value) p.append(key, v);
        } else {
          p.append(key, value);
        }
        return p;
      },
      { replace: true }
    );
  }

  function setParam(key: string, value: string | null) {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value == null || value === '') p.delete(key);
        else p.set(key, value);
        return p;
      },
      { replace: true }
    );
  }

  function clearAll() {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
  }

  const hasAnyFilter =
    Boolean(search) ||
    classification.length > 0 ||
    league.length > 0 ||
    priority.length > 0 ||
    hasInjury ||
    status !== 'active' ||
    group !== 'ALL' ||
    sort !== 'last_name';
  const showRosterAboveActivity = hasAnyFilter;
  const rosterResults = (
    <div className={showRosterAboveActivity ? 'order-3' : 'order-5'}>
      {isLoading && <RowSkeletons />}
      {isError && (
        <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
          Couldn&apos;t load players.
        </div>
      )}
      {data && data.items.length === 0 && (
        <div className="py-12 text-center text-sm text-muted">No matches.</div>
      )}
      {data && data.items.length > 0 && (
        <div className="surface overflow-hidden rounded-control">
          {data.items.map((p) => (
            <PlayerRosterRow key={p.id} player={p} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <section className="flex flex-col gap-5">
      {addingProfile && <AddProfileModal onClose={() => setAddingProfile(false)} />}
      <div className="surface order-2 overflow-hidden rounded-control md:order-1">
        <button
          type="button"
          onClick={() => setSummaryOpen((value) => !value)}
          className="flex w-full min-w-0 items-center justify-between gap-3 p-4 text-left md:p-5"
          aria-expanded={summaryOpen}
        >
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Virtual File Cabinet
            </p>
            <h1 className="mt-1 text-xl font-semibold tracking-normal md:text-2xl">People snapshot</h1>
          </div>
          <div className="flex shrink-0 items-center gap-2 font-mono text-[11px] uppercase text-muted">
            <span>{summaryOpen ? 'Hide' : 'Show'}</span>
            <ChevronDown size={16} className={summaryOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </div>
        </button>
        {summaryOpen && (
          <div className="grid w-full min-w-0 grid-cols-1 gap-2 border-t border-border/80 p-4 sm:grid-cols-2 md:p-5 lg:grid-cols-4">
            <SummaryTile icon={UsersRound} label="Active files" value={String(activeCount)} />
            <SummaryTile icon={ShieldCheck} label="NHL clients" value={String(nhlCount)} />
            <SummaryTile icon={UsersRound} label="Prospects" value={String(prospectCount)} />
            <SummaryTile icon={AlertCircle} label="IR watch" value={String(injuredCount)} tone={injuredCount > 0 ? 'warn' : 'default'} />
          </div>
        )}
      </div>

      <div className="surface-soft order-1 flex flex-col gap-4 rounded-control p-4 md:order-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold md:hidden">
              People
            </p>
            <h2 className="text-base font-semibold tracking-normal">Roster index</h2>
          </div>
          <div className="flex items-center gap-3">
            <p className="hidden font-mono text-[11px] text-muted sm:block">{data ? `${data.total} profiles` : 'loading…'}</p>
            <button type="button" onClick={() => setAddingProfile(true)} className="focus-ring inline-flex h-10 items-center gap-2 rounded-control bg-bartlett px-3 text-sm font-semibold text-white"><Plus size={16} /> Add profile</button>
          </div>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <label className="relative block min-w-0 flex-1">
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              type="search"
              placeholder="Search by name"
              className="focus-ring h-10 w-full rounded-control border border-border bg-white pl-9 pr-3 text-sm outline-none focus:border-bartlett"
              autoComplete="off"
            />
          </label>

          <div className="grid gap-2 sm:flex sm:items-center">
            <label className="flex items-center gap-2">
              <span className="w-14 text-[12px] uppercase tracking-wide text-muted sm:w-auto">Sort</span>
              <select
                value={sort}
                onChange={(e) => setParam('sort', e.target.value)}
                className="h-10 min-w-0 flex-1 rounded-control border border-border bg-white px-2 text-sm outline-none focus:border-bartlett sm:flex-none"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 sm:hidden">
              <span className="w-14 text-[12px] uppercase tracking-wide text-muted">Group</span>
              <select
                value={group}
                onChange={(e) => setParam('group', e.target.value === 'ALL' ? null : e.target.value)}
                className="h-10 min-w-0 flex-1 rounded-control border border-border bg-white px-2 text-sm outline-none focus:border-bartlett"
              >
                {PEOPLE_GROUPS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label} ({groupCounts.get(g.value) ?? 0})
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="hidden gap-2 overflow-x-auto pb-1 sm:flex">
          {PEOPLE_GROUPS.map((g) => (
            <button
              key={g.value}
              type="button"
              onClick={() => setParam('group', g.value === 'ALL' ? null : g.value)}
              className={[
                'focus-ring inline-flex h-10 shrink-0 items-center gap-2 rounded-control border px-3 font-mono text-[12px] font-semibold uppercase transition-all',
                group === g.value
                  ? 'border-bartlett bg-bartlett text-white shadow-sm'
                  : 'border-border bg-white text-ink hover:-translate-y-0.5 hover:border-bartlett/40 hover:shadow-sm',
              ].join(' ')}
            >
              <span>{g.label}</span>
              <span className={group === g.value ? 'text-white/75' : 'text-muted'}>
                {groupCounts.get(g.value) ?? 0}
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {CLASSIFICATIONS.map((c) => {
            const active = classification.includes(c.value);
            return (
              <Chip
                key={c.value}
                active={active}
                onClick={() => toggleParam('classification', c.value)}
              >
                {c.label}
              </Chip>
            );
          })}

          {showPriority && (
            <>
              <span className="mx-1 text-[11px] uppercase tracking-wide text-muted">priority</span>
              {PRIORITIES.map((p) => {
                const active = priority.includes(p.value);
                return (
                  <Chip key={p.value} active={active} onClick={() => toggleParam('priority', p.value)}>
                    {p.label}
                  </Chip>
                );
              })}
            </>
          )}

          <Chip active={hasInjury} onClick={() => setParam('hasInjury', hasInjury ? null : '1')}>
            injured
          </Chip>

          <Chip
            active={status === 'archived'}
            onClick={() => setParam('status', status === 'archived' ? null : 'archived')}
          >
            archived
          </Chip>

          {hasAnyFilter && (
            <button
              onClick={clearAll}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-0.5 font-mono text-[11px] text-muted hover:border-border hover:text-ink"
            >
              <X size={12} />
              clear
            </button>
          )}
        </div>
      </div>

      {showRosterAboveActivity && rosterResults}

      <div className="surface-soft order-4 rounded-control p-4">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-normal">Last 7 Days Bartlett Activity</h2>
            <p className="mt-1 text-sm text-muted">
              Notes, viewing logs, videos, and schedule entries added to client files by the Bartlett team.
            </p>
          </div>
          <Link
            to="/archive"
            className="focus-ring inline-flex h-9 w-fit items-center rounded-control border border-border bg-white px-3 text-sm font-semibold text-ink hover:bg-bartlettIce"
          >
            Review prior entries
          </Link>
        </div>
        {activity.isLoading ? (
          <div className="h-24 animate-pulse rounded-control border border-border bg-white" />
        ) : activity.isError ? (
          <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
            Couldn&apos;t load Bartlett activity.
          </div>
        ) : (
          <ClientActivityWall
            items={activity.data?.items ?? []}
            compact
            empty="No Bartlett profile notes or file additions have been added in the last seven days."
          />
        )}
      </div>

      {!showRosterAboveActivity && rosterResults}
    </section>
  );
}

function SummaryTile({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: typeof UsersRound;
  label: string;
  value: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="min-w-0 rounded-control border border-border bg-bartlettIce px-3 py-2">
      <div className="flex items-center gap-1.5 text-muted">
        <Icon size={13} />
        <span className="truncate font-mono text-[10px] uppercase tracking-[0.12em]">{label}</span>
      </div>
      <div className={['mt-1 font-mono text-[18px] font-semibold', tone === 'warn' ? 'text-flag-warn' : 'text-ink'].join(' ')}>
        {value}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex h-7 items-center rounded-full border px-3 text-[13px] font-medium transition-colors',
        active
          ? 'border-bartlett bg-bartlett text-white'
          : 'border-border bg-white text-ink hover:border-bartlett/40',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function RowSkeletons() {
  return (
    <div className="overflow-hidden rounded-control border border-border bg-white">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 border-b border-border px-4 py-3">
          <div className="h-9 w-9 animate-pulse rounded-full bg-border/60" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/3 animate-pulse rounded bg-border/60" />
            <div className="h-2.5 w-2/3 animate-pulse rounded bg-border/40" />
          </div>
        </div>
      ))}
    </div>
  );
}

function isEuropeLeague(league: string): boolean {
  return ['DEL', 'DEL2', 'KHL', 'NLA', 'ICE', 'EBEL', 'Czech', 'Austria', 'England', 'Liiga', 'SHL', 'Allsvenskan'].some(
    (token) => league.includes(token)
  );
}

function isNcaaLeague(league: string, team: string | null): boolean {
  return league.includes('NCAA') || (team ?? '').includes('Notre Dame');
}

function isJuniorLeague(league: string, team: string | null): boolean {
  return ['USHL', 'NAHL', 'OHL', 'WHL', 'QMJHL', 'BCHL', 'CCHL', 'NTDP'].some((token) => league.includes(token)) || (team ?? '').includes('NTDP');
}

function isUshlLeague(league: string): boolean {
  return league.includes('USHL');
}

function isWhlLeague(league: string): boolean {
  return league.includes('WHL');
}

function isChlLeague(league: string): boolean {
  return ['OHL', 'WHL', 'QMJHL'].some((token) => league.includes(token));
}

function isNtdpLeague(league: string, team: string | null): boolean {
  return league.includes('NTDP') || (team ?? '').includes('NTDP');
}
